import { DropdownMenuItem } from "@/lib/components/DropdownMenu";
import { Slider } from "@/lib/components/Slider";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";

function InfoTooltip({ label, info }: { label: string; info: string }) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`What does ${label} do?`}
          className="flex items-center text-text-secondary hover:text-text-primary"
          onClick={(e) => e.preventDefault()}
        >
          <IconInfoCircle size={12} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-80 text-center">{info}</TooltipContent>
    </Tooltip>
  );
}

export function SliderRow({
  label,
  info,
  value,
  onValueChange,
  onValueCommit,
  min,
  max,
  step,
  formatValue,
  valueWarning,
}: {
  label: string;
  info?: string;
  value: number;
  onValueChange: (v: number) => void;
  onValueCommit: (v: number) => void;
  min: number;
  max: number;
  step: number;
  formatValue: (v: number) => string;
  valueWarning?: string;
}) {
  return (
    <DropdownMenuItem
      onSelect={(e) => e.preventDefault()}
      className="flex cursor-default flex-col items-stretch gap-2 focus:bg-transparent"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-text-primary">{label}</span>
          {info && <InfoTooltip label={label} info={info} />}
        </div>
        {valueWarning ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="cursor-default text-xs text-error tabular-nums">
                {formatValue(value)}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-80 text-center">
              {valueWarning}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-xs text-text-secondary tabular-nums">
            {formatValue(value)}
          </span>
        )}
      </div>
      <div>
        <Slider
          value={value}
          onValueChange={onValueChange}
          onValueCommit={onValueCommit}
          min={min}
          max={Math.max(min + step, max)}
          step={step}
        />
      </div>
    </DropdownMenuItem>
  );
}

export function formatSeconds(ms: number) {
  return `${(ms / 1000).toFixed(1)}s`;
}

export function useDraftValue(external: number) {
  const [draft, setDraft] = useState<number | null>(null);
  return {
    value: draft ?? external,
    setDraft: (v: number) => setDraft(v),
    commit: () => setDraft(null),
  };
}
